import React, { useContext, useState } from 'react'
import FillButton from 'Components/Common/components/Button/FillButton'
import OutlineButton from 'Components/Common/components/Button/OutlineButton'
import Label from 'Components/Common/components/Label'
import ModalBody from 'Components/Common/Modal/ModalBody'
import ModalContainer from 'Components/Common/Modal/ModalContainer'
import ModalFooter from 'Components/Common/Modal/ModalFooter'
import ModalHeader from 'Components/Common/Modal/ModalHeader'
import { AppContext } from 'Context/AppContext'
import { importList, validateList } from './Services/ListServices'
import ProgressModal from './ProgressModal'
import ValidationErrorModal from './ValidationErrorModal'
import ValidationSuccessModal from './ValidationSuccessModal'
import { toast } from 'react-toastify'
import { CloseIcon, FileIcon } from 'assests/GmsIcons'

type ValidateModalProps = {
    isOpen: boolean,
    onClose: Function,
    listId: string | undefined
    action: ListAction
    fetchEditState: () => void
}

const ValidateModal = ({ isOpen, onClose, listId, action, fetchEditState }: ValidateModalProps) => {
    let appContext = useContext(AppContext);
    let industryId = appContext && appContext?.get_selected_industry() && appContext?.get_selected_industry()?.id;
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [validating, setValidating] = useState<boolean>(false);
    const [importing, setImporting] = useState<boolean>(false);
    const [validationResponse, setValidationResponse] = useState<ValidationResponse | undefined>(undefined);

    async function handle_file_selection(event: React.ChangeEvent<HTMLInputElement>) {
        event.preventDefault();
        const file = (event.target as any).files[0];
        const totalCount = await appContext.checkSelectedFile(file, file.type);
        if (totalCount > 0) {
            setSelectedFile(file);
        } else {
            setSelectedFile(null);
        }
    }

    const handleImport = async () => {
        if (!selectedFile || !industryId || !listId) return
        setImporting(true)
        try {
            const res = await importList(industryId, listId, selectedFile);
            if (res?.done) {
                fetchEditState();
                toast.success("Zero Errors");
            } else {
                toast.error(res?.message ?? "Failed import")
            }
        }
        finally {
            setImporting(false)
            setValidationResponse(undefined);
            onClose();
        }
    }

    const handleValidate = async () => {
        if (!selectedFile) return
        setValidating(true)
        try {
            const res = await validateList(selectedFile);
            if (res?.done) {
                setValidationResponse({ issueCount: res?.data?.issueCount, url: res?.data?.url, message: res?.message })
                if (res?.data?.issueCount === 0 && action === "Import") {
                    setValidating(false)
                    handleImport();
                }
            }
        }
        finally {
            if (validating) setValidating(false)
        }
    }

    if (validationResponse && validationResponse?.issueCount > 0) {
        return <ValidationErrorModal validationError={validationResponse} action={action} onClose={() => {
            setValidationResponse(undefined);
            onClose();
        }} />
    } else if (validationResponse && validationResponse?.issueCount === 0 && action === "Validate") {
        return < ValidationSuccessModal handleImport={handleImport} importing={importing} onClose={() => {
            setValidationResponse(undefined);
            onClose();
        }} />
    }

    return (
        <ModalContainer isOpen={isOpen} setOpen={onClose} width='500px'>
            <ModalHeader>
                <div>
                    <Label
                        message={action === "Validate" ? "Validate List" : "Import List"}
                        fontSize={"0.875"}
                        fontWeight="600"
                    />
                </div>
            </ModalHeader>

            <ModalBody>
                {!validating && !importing && <div className="tw-border tw-border-dashed tw-py-5 tw-relative">
                    {
                        !selectedFile &&
                        <label
                            className="cursor-pointer"
                            style={{
                                width: "100%",
                                textAlign: "center",
                                fontWeight: "400",
                                color: "#091CAA",
                            }}
                        >
                            <input
                                type="file"
                                id="fupload"
                                style={{ display: "none" }}
                                onChange={(event) => handle_file_selection(event)}
                            />
                            <Label
                                message={
                                    "(Note: Only .xlsx and .xls files are allowed to upload)"
                                }
                                color={"#172B4D"}
                                fontSize={"0.75"}
                                fontWeight={"400"}
                            />
                            <p>Browse File</p>
                        </label>
                    }

                    {
                        selectedFile && (
                            <div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-text-center tw-py-5">
                                <span className="">
                                    <FileIcon />
                                </span>
                                <span
                                    className="mt-2 w-100"
                                    style={{
                                        display: "block",
                                        width: "100%",
                                        textAlign: "center",
                                        wordWrap: "break-word",
                                        marginLeft: "auto",
                                        marginRight: "auto",
                                    }}
                                >
                                    {selectedFile.name}
                                </span>
                                <span
                                    className="tw-cursor-pointer tw-absolute tw-top-1 tw-right-1"
                                    onClick={(e) => {
                                        setSelectedFile(null);
                                    }}
                                >
                                    <CloseIcon />
                                </span>
                            </div>
                        )}
                </div>}
                {(validating || importing) && <ProgressModal action={validating ? "Validation" : "Import"} />}
            </ModalBody>

            {!validating && !importing && <ModalFooter>
                <div className="min-w-full tw-space-x-3 tw-justify-end tw-flex">
                    <OutlineButton onClick={() => onClose()}>Cancel</OutlineButton>
                    <FillButton onClick={handleValidate}>{action}</FillButton>
                </div>
            </ModalFooter>}
        </ModalContainer>
    )
}

export default ValidateModal